import { useEffect } from "react";
import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "theme";

function getSystemTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function getTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return getSystemTheme();
}

export function setTheme(theme: Theme): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEY, theme);
  applyTheme(theme);
  listeners.forEach((fn) => fn());
}

export function toggleTheme(): void {
  setTheme(getTheme() === "dark" ? "light" : "dark");
}

function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

const listeners = new Set<() => void>();

if (typeof window !== "undefined") {
  // Ensure DOM matches localStorage when module loads (belt-and-suspenders with inline script)
  applyTheme(getTheme());

  window.addEventListener("storage", (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) {
      applyTheme(getTheme());
      listeners.forEach((fn) => fn());
    }
  });
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

function getSnapshot(): Theme {
  return getTheme();
}

function getServerSnapshot(): Theme {
  return "dark";
}

export function useTheme(): {
  theme: Theme;
  setTheme: typeof setTheme;
  toggleTheme: typeof toggleTheme;
} {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { theme, setTheme, toggleTheme };
}

/**
 * Client component that keeps the `dark` class on <html> in sync with React
 * theme state. Guards against Next.js reconciliation stripping the class.
 */
export function ThemeSync(): null {
  const { theme } = useTheme();
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  return null;
}
